import { useState } from 'react';
import type { Status, Story } from '@/types/board';
import { COLUMNS } from '@/config/columns';
import {
  filterStories,
  findProject,
  isBlocked,
  storiesInColumn,
  storyProgress,
} from '@/store/selectors';
import { useBoard, useBoardStore } from '@/store/BoardContext';
import { useUi } from '@/store/UiContext';
import { useStoryMenu } from '@/components/cards/useItemMenus';
import { StatusToggle } from '@/components/ui/StatusToggle';
import { DueChip, PriorityChip, ProjectTag } from '@/components/ui/Badges';
import { FilterBar } from '@/components/board/FilterBar';
import { TaskList } from './TaskList';
import './StoryListView.css';

/**
 * The Tasks view — a calm, single-column list of every story.
 *
 * Deliberately not a second board. The board answers "where does everything
 * stand"; this answers "what is on my plate", so a row carries only the five
 * things you need to triage: state, title, project, priority, and due date.
 * Everything else stays one tap away in the detail modal.
 *
 * Filtering reuses `filterStories` and `storiesInColumn` rather than
 * re-implementing them, so this view and the board can never disagree about
 * which stories are visible.
 */

function StoryRow({ story }: { story: Story }) {
  const { board } = useBoard();
  const { moveStory, canEdit } = useBoardStore();
  const ui = useUi();
  const { openStoryMenu } = useStoryMenu();

  const project = findProject(board, story.project);
  const progress = storyProgress(board, story.id);
  const blocked = isBlocked(board, story);
  const expanded = ui.isExpanded(story.id);

  return (
    <div className="sl-row-wrap">
      <div
        className={`sl-row${story.status === 'done' ? ' done' : ''}`}
        data-story-id={story.id}
        onContextMenu={(e) => openStoryMenu(e, story)}
      >
        <StatusToggle
          status={story.status}
          size="lg"
          disabled={!canEdit}
          onAdvance={(next) => moveStory(story.id, next)}
        />

        {/*
          A button rather than a click handler on the row, so the primary action
          is reachable by keyboard and announced as actionable. The chevron and
          the status dot are separate buttons outside it — nesting them would be
          invalid and would swallow their clicks.
        */}
        <button className="sl-open" onClick={() => ui.openDetail(story.id)}>
          <span className="sl-title">{story.title}</span>
        </button>

        <div className="sl-meta">
          {blocked ? (
            <span className="sl-blocked" title="Blocked by an unfinished story">
              ⚠
            </span>
          ) : null}
          <ProjectTag project={project} />
          {story.priority !== 'medium' ? <PriorityChip priority={story.priority} /> : null}
          <DueChip due={story.due} status={story.status} />
        </div>

        {progress.total > 0 ? (
          <button
            className={`sl-caret${expanded ? ' open' : ''}`}
            onClick={() => ui.toggleExpanded(story.id)}
            aria-expanded={expanded}
            title={expanded ? 'Hide tasks' : 'Show tasks'}
          >
            <span className="sl-count">
              {progress.done}/{progress.total}
            </span>
            <span className="caret">▾</span>
          </button>
        ) : (
          /* Keeps the chevron column aligned when a story has no tasks. */
          <span className="sl-caret placeholder" aria-hidden="true" />
        )}
      </div>

      {expanded && progress.total > 0 ? (
        <div className="sl-tasks">
          <TaskList tasks={board.tasks.filter((t) => t.storyId === story.id)} storyId={story.id} />
        </div>
      ) : null}
    </div>
  );
}

function StatusGroup({
  status,
  stories,
  collapsed,
  onToggle,
}: {
  status: Status;
  stories: Story[];
  collapsed: boolean;
  onToggle: () => void;
}) {
  const def = COLUMNS.find((c) => c.id === status)!;

  return (
    <section className="sl-group" data-status={status}>
      <button
        className={`sl-group-head${collapsed ? '' : ' open'}`}
        onClick={onToggle}
        aria-expanded={!collapsed}
      >
        <span className="caret">▾</span>
        <span className="sl-group-title">{def.title}</span>
        <span className="sl-group-count">{stories.length}</span>
      </button>

      {!collapsed ? (
        <div className="sl-rows">
          {stories.length === 0 ? (
            <div className="sl-empty">Nothing here</div>
          ) : (
            stories.map((s) => <StoryRow key={s.id} story={s} />)
          )}
        </div>
      ) : null}
    </section>
  );
}

export function StoryListView() {
  const { board } = useBoard();
  const ui = useUi();
  const [showDone, setShowDone] = useState(false);

  // Group collapse is view furniture, not app state — it is deliberately local,
  // so it resets when you leave and does not need to be persisted.
  // Done starts collapsed: it is the one group you rarely came here to read.
  const [collapsed, setCollapsed] = useState<Record<string, boolean>>({ done: true });

  const visible = filterStories(board, ui.filters);
  const groups = COLUMNS.filter((c) => c.id !== 'done' || showDone);
  const doneCount = storiesInColumn(visible, 'done').length;

  return (
    <div className="story-list-view">
      <FilterBar />

      <div className="sl-toolbar">
        <label className="sl-toggle">
          <input
            type="checkbox"
            checked={showDone}
            onChange={(e) => setShowDone(e.target.checked)}
          />
          Show done
          {doneCount > 0 ? <span className="sl-toggle-count">{doneCount}</span> : null}
        </label>
      </div>

      {visible.length === 0 ? (
        <div className="sl-none">
          {board.stories.length === 0 ? (
            <>
              <p>Nothing on your list yet.</p>
              <button className="ghost" onClick={() => ui.openStoryEditor({})}>
                Add the first one
              </button>
            </>
          ) : (
            <>
              <p>Nothing matches the current filters.</p>
              <button className="ghost" onClick={ui.clearFilters}>
                Clear filters
              </button>
            </>
          )}
        </div>
      ) : (
        <div className="sl-groups">
          {groups.map((c) => (
            <StatusGroup
              key={c.id}
              status={c.id}
              stories={storiesInColumn(visible, c.id)}
              collapsed={collapsed[c.id] ?? false}
              onToggle={() => setCollapsed((m) => ({ ...m, [c.id]: !m[c.id] }))}
            />
          ))}
        </div>
      )}
    </div>
  );
}
